import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { NGXLogger } from 'ngx-logger';
import moment from 'moment';

import { GT2PageAbstract } from '../../../content/abstract/GT2PageAbstract';
import { FuseTranslationLoaderService } from '../../../../../@fuse/services/translation-loader.service';
import { EventsService } from '../../../api/events.service';
import { ClientsService } from '../../../api/clients.service';
import { QuickOrderPanelService } from '../../../services/quick-order-panel.service';
import { Settings } from '../../../settings/settings';
import { GT2CalendarUtils } from '../../../utils/GT2CalendarUtils';
import { GT2DateAdapter } from '../../../utils/GT2DateAdapter';
import { locale as english } from './i18n/en-CA';
import { locale as french } from './i18n/fr-CA';

@Component({
  selector: 'app-quick-order',
  templateUrl: './quick-order.component.html',
  styleUrls: ['./quick-order.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatInputModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatOptionModule,
    NgxMaterialTimepickerModule,
    TranslateModule,
  ],
  providers: [{ provide: DateAdapter, useClass: GT2DateAdapter }],
})
export class QuickOrderComponent
  extends GT2PageAbstract
  implements OnInit, OnDestroy
{
  form: UntypedFormGroup;
  event: any = {
    name: null,
    event_date: moment().format('YYYY-MM-DD'),
    event_date_end: null,
    event_time: null,
    event_time_end: null,
  };
  override loading: boolean = false; // save in progress
  startTimeSelected: boolean = false;
  eventDateTimeRangeInvalid: boolean = false;

  // Client selection (autocomplete "select control")
  clientType: 'client' | 'enterprise' = 'client';
  orderingSearchCtrl: UntypedFormControl = new UntypedFormControl('');
  invoicingSearchCtrl: UntypedFormControl = new UntypedFormControl('');
  orderingClient: any = null;
  invoicingClient: any = null;
  orderingResults: any[] = [];
  invoicingResults: any[] = [];
  orderingSearching: boolean = false;
  invoicingSearching: boolean = false;
  // When true, the invoicing client mirrors the ordering client.
  sameClient: boolean = false;

  private orderingSearch$ = new Subject<string>();
  private invoicingSearch$ = new Subject<string>();
  private subs: Subscription[] = [];

  constructor(
    private panelService: QuickOrderPanelService,
    private translationLoader: FuseTranslationLoaderService,
    public translate: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private eventsService: EventsService,
    private clientsService: ClientsService,
    private adapter: DateAdapter<any>,
    public dialog: MatDialog,
    public router: Router,
    private logger: NGXLogger,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);
    this.adapter.setLocale(this.translate.currentLang);

    const data = this.panelService.getData();
    if (data?.event_date) {
      this.event.event_date = moment(data.event_date).format('YYYY-MM-DD');
    }

    this.form = this.formBuilder.group({
      name: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxCharMid),
          Validators.required,
        ],
      ],
      event_date: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.subs.push(
      this.orderingSearch$
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe((value) => this.doSearch(value, 'ordering')),
    );
    this.subs.push(
      this.invoicingSearch$
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe((value) => this.doSearch(value, 'invoicing')),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.subs = [];
  }

  // -----------------------------------------------------------------------------
  // Date / time (mirrors CreateEventComponent behaviour)
  // -----------------------------------------------------------------------------
  public onDatePickerChange(event: any): void {
    if (!event.value) {
      return;
    }
    const date: Date = new Date(event.value);
    this.event.event_date = GT2CalendarUtils.formatDateForAPI(date);
    this.refreshEventDateTimeRangeInvalid();
  }

  public onDatePickerChangeEnd(event: any): void {
    if (!event.value) {
      this.event.event_date_end = null;
      this.refreshEventDateTimeRangeInvalid();
      return;
    }
    const date: Date = new Date(event.value);
    this.event.event_date_end = GT2CalendarUtils.formatDateForAPI(date);
    this.refreshEventDateTimeRangeInvalid();
  }

  // Small "+3h"-sized button: copy the start date into the end date.
  public copyStartDateToEnd(): void {
    this.event.event_date_end = this.event.event_date;
    this.refreshEventDateTimeRangeInvalid();
  }

  public onStartTimeChange(time: string): void {
    this.event.event_time = time;
    this.startTimeSelected = !!(
      time &&
      time !== '' &&
      time !== 'Invalid DateTime'
    );
    this.refreshEventDateTimeRangeInvalid();
  }

  public onEndTimeChange(_time?: string): void {
    this.refreshEventDateTimeRangeInvalid();
  }

  public addThreeHoursToEndTime(): void {
    if (this.event.event_time && this.event.event_time !== 'Invalid DateTime') {
      const startTime = moment(this.event.event_time, 'HH:mm');
      this.event.event_time_end = startTime
        .clone()
        .add(3, 'hours')
        .format('HH:mm');
      this.refreshEventDateTimeRangeInvalid();
    }
  }

  public refreshEventDateTimeRangeInvalid(): void {
    const t0 = this.event?.event_time;
    const t1 = this.event?.event_time_end;
    if (!t0 || t0 === 'Invalid DateTime' || !t1 || t1 === 'Invalid DateTime') {
      this.eventDateTimeRangeInvalid = false;
      return;
    }
    const dateStart = this.event?.event_date;
    const dateEnd = this.event?.event_date_end || dateStart;
    const start = moment(`${dateStart} ${t0}`, 'YYYY-MM-DD HH:mm', true);
    const end = moment(`${dateEnd} ${t1}`, 'YYYY-MM-DD HH:mm', true);
    this.eventDateTimeRangeInvalid =
      start.isValid() && end.isValid() ? end.isBefore(start) : false;
  }

  // -----------------------------------------------------------------------------
  // Client autocomplete
  // -----------------------------------------------------------------------------
  public onClientTypeChange(): void {
    // Switching the search scope clears current selections to avoid mixing
    // client/enterprise results.
    this.clearOrdering();
    this.clearInvoicing();
    this.sameClient = false;
  }

  public onOrderingInput(value: any): void {
    if (typeof value === 'string') {
      this.orderingSearch$.next(value);
    }
  }

  public onInvoicingInput(value: any): void {
    if (typeof value === 'string') {
      this.invoicingSearch$.next(value);
    }
  }

  private doSearch(keyword: string, field: 'ordering' | 'invoicing'): void {
    if (!keyword || keyword.length < 2) {
      if (field === 'ordering') {
        this.orderingResults = [];
      } else {
        this.invoicingResults = [];
      }
      return;
    }
    const obs =
      this.clientType === 'enterprise'
        ? this.clientsService.getAllEnterprisesClients(keyword)
        : this.clientsService.getAllClientsCC(keyword);

    if (field === 'ordering') {
      this.orderingSearching = true;
    } else {
      this.invoicingSearching = true;
    }

    obs.subscribe({
      next: (response: any) => {
        if (field === 'ordering') {
          this.orderingResults = response?.data || [];
          this.orderingSearching = false;
        } else {
          this.invoicingResults = response?.data || [];
          this.invoicingSearching = false;
        }
      },
      error: (error: any) => {
        this.logger.error(
          'QuickOrderComponent.doSearch() -> ERROR: ' + JSON.stringify(error),
        );
        if (field === 'ordering') {
          this.orderingSearching = false;
        } else {
          this.invoicingSearching = false;
        }
        this.handleAPIError(error, this.dialog, null, null, this.router);
      },
    });
  }

  public displayClient = (client: any): string => {
    if (!client) {
      return '';
    }
    // While typing, the control value is the raw string the user entered.
    if (typeof client === 'string') {
      return client;
    }
    return client.name_label ?? '';
  };

  public onOrderingSelected(client: any): void {
    this.orderingClient = client;
    if (this.sameClient) {
      this.invoicingClient = client;
      this.invoicingSearchCtrl.setValue(client, { emitEvent: false });
    }
  }

  public onInvoicingSelected(client: any): void {
    this.invoicingClient = client;
  }

  public clearOrdering(): void {
    this.orderingClient = null;
    this.orderingResults = [];
    this.orderingSearchCtrl.setValue('', { emitEvent: false });
  }

  public clearInvoicing(): void {
    this.invoicingClient = null;
    this.invoicingResults = [];
    this.invoicingSearchCtrl.setValue('', { emitEvent: false });
  }

  // Yellow button: use the ordering client for both ordering & invoicing.
  public toggleSameClient(): void {
    this.sameClient = !this.sameClient;
    if (this.sameClient) {
      this.invoicingClient = this.orderingClient;
      this.invoicingSearchCtrl.setValue(this.orderingClient, {
        emitEvent: false,
      });
      this.invoicingSearchCtrl.disable({ emitEvent: false });
    } else {
      this.invoicingSearchCtrl.enable({ emitEvent: false });
    }
  }

  // -----------------------------------------------------------------------------
  // Save / cancel
  // -----------------------------------------------------------------------------
  public get canSave(): boolean {
    return (
      this.form.valid &&
      !!this.orderingClient &&
      !!this.invoicingClient &&
      this.event.event_time &&
      this.event.event_time !== 'Invalid DateTime' &&
      this.event.event_time_end !== 'Invalid DateTime' &&
      !this.eventDateTimeRangeInvalid &&
      !this.loading
    );
  }

  public onSave(): void {
    if (!this.canSave) {
      return;
    }
    this.loading = true;
    const payload = {
      name: this.event.name,
      event_date: this.event.event_date,
      event_date_end: this.event.event_date_end,
      event_time: this.event.event_time,
      event_time_end: this.event.event_time_end,
      ordering_client_uuid: this.orderingClient?.uuid,
      invoicing_client_uuid: this.invoicingClient?.uuid,
    };
    this.eventsService.createQuickOrder(payload).subscribe({
      next: (response: any) => {
        this.loading = false;
        this.panelService.emitResult(response.data);
      },
      error: (error: any) => {
        this.logger.error(
          'QuickOrderComponent.onSave() -> ERROR: ' + JSON.stringify(error),
        );
        this.loading = false;
        this.handleAPIError(error, this.dialog, null, null, this.router);
      },
    });
  }

  public close(): void {
    this.panelService.emitResult(false);
  }
}
